import {
  addBeforeItem,
  addDocument,
  AmountField,
  ConfirmDialog,
  countryName,
  countryOptions,
  DateRangeInput,
  Dialog,
  documentFileUrl,
  documentKinds,
  formatMoney,
  getDocs,
  InvalidInput,
  NotFound,
  Pick,
  removeBeforeItem,
  removeDocument,
  removeDocumentFile,
  setPassport,
  tickEntry,
  toIso,
  updateBeforeItem,
  updateDocument,
  uploadDocumentFile,
  type DocsScreen,
  type DocumentKind,
  type TripDocument,
} from '@trippath/shared';
import { addMonths, differenceInCalendarDays } from 'date-fns';
import { useEffect, useId, useRef, useState, type SyntheticEvent } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { day, problemKey } from '../../lib/format';
import { RuleEdit } from './RuleEdit';
import { keepFiles, workerActive } from '../../app/offline';
import { useLive } from '../../app/live';
import { useReadOnly } from '../../app/readOnly';

/** For what fails on purpose quietly: the page shows what it had. */
const ignore = () => undefined;

/** The days a document is valid, as its line says them: `1 Oct 2026 – 30 Apr 2027`, `valid to
 * 30 Apr 2031`, `valid from 1 Oct 2026`; none when it says none. */
function validitySaid(document: TripDocument, t: TFunction, lang: string): string | null {
  const { validFrom: from, validTo: to } = document;
  if (from !== null && to !== null) return `${day(from, lang)} – ${day(to, lang)}`;
  if (to !== null) return t('docs.validUntil', { day: day(to, lang) });
  return from === null ? null : t('docs.validSince', { day: day(from, lang) });
}

type State =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'failed' }
  | { kind: 'ready'; screen: DocsScreen };

/**
 * Documents (the Docs mockup): the entry rules of the route for the traveller's passport with a
 * tick for each border's task, the trip's documents and their files, and the list of what to do
 * before leaving.
 */
export function Docs() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  // Read again when someone else changes the trip.
  const { version: changed } = useLive();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [version, setVersion] = useState(0);
  const reload = () => {
    setVersion((was) => was + 1);
  };

  useEffect(() => {
    let live = true;
    getDocs(id).then(
      (screen) => {
        if (live) setState({ kind: 'ready', screen });
      },
      (error: unknown) => {
        if (live) setState({ kind: error instanceof NotFound ? 'missing' : 'failed' });
      },
    );
    return () => {
      live = false;
    };
  }, [id, version, changed]);

  if (state.kind === 'loading') return <p className="page quiet">{t('trips.loading')}</p>;
  if (state.kind !== 'ready') {
    return (
      <div className="page">
        <p role="alert">{t(state.kind === 'missing' ? 'trips.notFound' : 'trips.failed')}</p>
        <Link to="/">{t('trips.back')}</Link>
      </div>
    );
  }
  const { screen } = state;

  return (
    <div className="page docs">
      <h1 className="visually-hidden">{t('docs.title')}</h1>
      <div className="docs-grid">
        <div className="docs-main">
          <EntryRules trip={id} screen={screen} onChanged={reload} />
          <Documents trip={id} screen={screen} onChanged={reload} />
        </div>
        <BeforeList trip={id} screen={screen} onChanged={reload} />
      </div>
    </div>
  );
}

/** The route's borders for the passport: the stay against the most allowed, and what to do. */
function EntryRules({
  trip,
  screen,
  onChanged,
}: {
  trip: string;
  screen: DocsScreen;
  onChanged: () => void;
}) {
  const { t, i18n } = useTranslation();
  // A trip read only: said, nothing ticked, added or changed.
  const locked = useReadOnly();
  const lang = i18n.language;
  const ids = useId();
  const today = toIso(new Date());

  return (
    <section className="panel docs-rules" aria-labelledby={`${ids}-title`}>
      <div className="docs-head">
        <div>
          <h2 id={`${ids}-title`}>{t('docs.rules')}</h2>
          <p className="quiet">{t('docs.rulesLead')}</p>
        </div>
        <Pick
          label={t('docs.passport')}
          options={countryOptions(lang)}
          value={screen.passport}
          searchable
          onChange={(code) => {
            setPassport(code).then(onChanged, ignore);
          }}
          onClear={() => {
            setPassport(null).then(onChanged, ignore);
          }}
        />
      </div>
      {screen.passport === null && <p className="docs-hint">{t('docs.noPassport')}</p>}
      {screen.crossings.length === 0 ? (
        <p className="quiet">{t('docs.noCountries')}</p>
      ) : (
        <div className="docs-table">
          <table>
            <thead>
              <tr>
                <th scope="col">{t('docs.country')}</th>
                <th scope="col">{t('docs.border')}</th>
                <th scope="col">{t('docs.stay')}</th>
                <th scope="col">{t('docs.todo')}</th>
                <th scope="col">{t('docs.reminder')}</th>
                <th scope="col">
                  <span className="visually-hidden">{t('docs.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {screen.crossings.map((crossing) => {
                const country = countryName(crossing.country, lang);
                const rule = crossing.rule;
                const over = rule ? crossing.days - rule.days : 0;
                const todo =
                  screen.passport === null
                    ? t('docs.pickPassport')
                    : rule
                      ? t(`docs.kind.${rule.kind}`, { form: rule.form })
                      : t('docs.unknown');
                const late = !crossing.done && crossing.due !== null && crossing.due < today;
                return (
                  <tr key={`${crossing.country}-${String(crossing.entry)}`}>
                    <th scope="row">
                      {country}
                      {crossing.unresolved && (
                        <span className="quiet"> · {t('docs.undecided')}</span>
                      )}
                    </th>
                    <td className="mono">{day(crossing.border, lang)}</td>
                    <td>
                      {rule && rule.days > 0 ? (
                        <>
                          <span className="mono">
                            {t('docs.days', { days: crossing.days, max: rule.days })}
                          </span>
                          <span className="docs-bar" aria-hidden="true">
                            <span
                              className={over > 0 ? 'over' : ''}
                              style={{
                                width: `${String(Math.min(100, Math.round((crossing.days / rule.days) * 100)))}%`,
                              }}
                            />
                          </span>
                          {over > 0 && (
                            <span className="field-error">
                              {t('docs.over', { count: over })}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="mono">{crossing.days}</span>
                      )}
                    </td>
                    <td className={crossing.due !== null && !crossing.done ? 'docs-todo' : ''}>
                      {todo}
                      {rule && rule.source !== 'own' && (
                        <>
                          {' · '}
                          {/* Where it is said: the official page, or Wikipedia's. */}
                          <a
                            href={rule.url}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={t(
                              rule.source === 'list' ? 'docs.officialFor' : 'docs.wikipediaFor',
                              { country },
                            )}
                          >
                            {t(rule.source === 'list' ? 'docs.official' : 'docs.wikipedia')}
                          </a>
                        </>
                      )}
                      {rule?.source === 'own' && (
                        <span className="quiet"> · {t('docs.yours')}</span>
                      )}
                    </td>
                    <td>
                      {crossing.due !== null && (
                        <label className={`check${late ? ' docs-late' : ''}`}>
                          <input
                            type="checkbox"
                            checked={crossing.done}
                            disabled={locked}
                            aria-label={t('docs.tick', { todo, country })}
                            onChange={(event) => {
                              tickEntry(
                                trip,
                                crossing.country,
                                crossing.entry,
                                event.target.checked,
                              ).then(onChanged, ignore);
                            }}
                          />
                          {crossing.done
                            ? t('docs.doneLabel')
                            : t('docs.due', { day: day(crossing.due, lang) })}
                        </label>
                      )}
                    </td>
                    <td className="docs-row-menu">
                      {/* One's own rule, behind the row's menu. */}
                      {screen.passport !== null && !locked && (
                        <RuleEdit
                          country={crossing.country}
                          name={country}
                          rule={rule}
                          onChanged={onChanged}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="quiet docs-note">{t('docs.indicative')}</p>
    </section>
  );
}

/** What the document dialog opens with. */
interface Editing {
  document: TripDocument | null;
  kind: DocumentKind;
  file: File | null;
  /** Whose it is: a fellow traveller's, or `null` for one's own. */
  traveller: string | null;
}

/** The trip's documents: who you are and your cover, then every document with its file. */
function Documents({
  trip,
  screen,
  onChanged,
}: {
  trip: string;
  screen: DocsScreen;
  onChanged: () => void;
}) {
  const { t, i18n } = useTranslation();
  // A trip read only: said, nothing ticked, added or changed.
  const locked = useReadOnly();
  const lang = i18n.language;
  const ids = useId();
  const scan = useRef<HTMLInputElement>(null);
  const upload = useRef<HTMLInputElement>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [removing, setRemoving] = useState<TripDocument | null>(null);
  const [kept, setKept] = useState(false);
  // Whose documents are shown: one's own, or a fellow traveller's.
  const [person, setPerson] = useState<string | null>(null);
  const documents = screen.documents.filter((d) => d.traveller === person);
  const of = (kind: DocumentKind) => documents.find((d) => d.kind === kind);
  const passport = documents.find((d) => d.kind === 'passport' && (person !== null || d.mine));
  const insurance = of('insurance');
  const permit = of('permit');
  const vaccination = of('vaccination');
  const files = documents.filter((d) => d.file !== null);
  const stopName = (stop: string | null) => screen.stops.find((s) => s.id === stop)?.city;

  const chosen = (file: File | undefined) => {
    if (file) setEditing({ document: null, kind: 'other', file, traveller: person });
  };
  // The six-month rule: the passport is valid six months past the trip's last day.
  const enough = toIso(addMonths(new Date(`${screen.end}T00:00`), 6));
  const passportState = !passport
    ? 'notAdded'
    : passport.validTo === null
      ? 'passportNoDate'
      : passport.validTo >= enough
        ? 'passportOk'
        : 'passportShort';
  const passportDay = passport?.validTo ? day(passport.validTo, lang) : '';

  return (
    <section className="panel docs-documents" aria-labelledby={`${ids}-title`}>
      <div className="docs-head">
        <h2 id={`${ids}-title`}>{t('docs.documents')}</h2>
        {!locked && (
          <div className="docs-actions">
            <button
              type="button"
              className="button"
              onClick={() => {
                setEditing({ document: null, kind: 'other', file: null, traveller: person });
              }}
            >
              {t('docs.addPlain')}
            </button>
            <button
              type="button"
              className="button"
              onClick={() => {
                scan.current?.click();
              }}
            >
              {t('docs.scan')}
            </button>
            <button
              type="button"
              className="button button-primary"
              onClick={() => {
                upload.current?.click();
              }}
            >
              {t('docs.upload')}
            </button>
          </div>
        )}
      </div>
      <input
        ref={scan}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        tabIndex={-1}
        aria-hidden="true"
        data-testid="doc-scan"
        onChange={(event) => {
          chosen(event.target.files?.[0]);
          event.target.value = '';
        }}
      />
      <input
        ref={upload}
        type="file"
        accept="application/pdf,image/*"
        hidden
        tabIndex={-1}
        aria-hidden="true"
        data-testid="doc-upload"
        onChange={(event) => {
          chosen(event.target.files?.[0]);
          event.target.value = '';
        }}
      />
      {/* Each person's own: one's own first, then each fellow traveller's. */}
      {screen.travellers.length > 0 && (
        <div className="segmented docs-people" role="tablist" aria-label={t('docs.whose')}>
          {[{ id: null, name: t('docs.mine') }, ...screen.travellers].map((one) => (
            <button
              key={one.id ?? ''}
              type="button"
              role="tab"
              aria-selected={person === one.id}
              onClick={() => {
                setPerson(one.id);
              }}
            >
              {one.name}
            </button>
          ))}
        </div>
      )}
      <div className="docs-columns">
        <div className="docs-identity">
          <h3>{t('docs.identity')}</h3>
          <p className="docs-row">
            <span>{t('docs.kinds.passport')}</span>
            <span
              className={
                passportState === 'passportOk'
                  ? 'docs-ok'
                  : passportState === 'passportShort'
                    ? 'field-error'
                    : 'quiet'
              }
            >
              {t(`docs.${passportState}`, { day: passportDay })}
            </span>
          </p>
          {insurance ? (
            <p className="docs-row">
              <span>{t('docs.kinds.insurance')}</span>
              <span className="docs-ok">{insurance.name}</span>
            </p>
          ) : (
            <div className="docs-row docs-missing">
              <span>{t('docs.insuranceMissing')}</span>
              {!locked && (
                <button
                  type="button"
                  className="button"
                  onClick={() => {
                    setEditing({
                      document: null,
                      kind: 'insurance',
                      file: null,
                      traveller: person,
                    });
                  }}
                >
                  {t('docs.addPolicy')}
                </button>
              )}
            </div>
          )}
          <p className="docs-row">
            <span>{t('docs.kinds.permit')}</span>
            <span className="quiet">{permit ? permit.name : t('docs.optional')}</span>
          </p>
          <p className="docs-row">
            <span>{t('docs.kinds.vaccination')}</span>
            <span className="quiet">{vaccination ? vaccination.name : t('docs.notAdded')}</span>
          </p>
        </div>
        <div className="docs-list">
          <h3>{t('docs.linked')}</h3>
          {documents.length === 0 ? (
            <p className="quiet">{t('docs.noDocuments')}</p>
          ) : (
            <ul>
              {documents.map((document) => {
                const stop = stopName(document.stop);
                const owner = document.owner?.name ?? t('activity.someone');
                // What it cost: in its own money, and the trip's when paid in another.
                const cost =
                  document.price &&
                  (document.paid
                    ? `${formatMoney(document.paid, lang)} (${formatMoney(document.price, lang)})`
                    : formatMoney(document.price, lang));
                const meta = [
                  t(`docs.kinds.${document.kind}`),
                  cost,
                  stop,
                  validitySaid(document, t, lang),
                  document.note,
                  document.private
                    ? t('docs.privateToYou')
                    : !document.mine && t('docs.sharedBy', { name: owner }),
                ].filter(Boolean);
                return (
                  <li key={document.id}>
                    <div className="docs-what">
                      {document.file ? (
                        <a
                          href={documentFileUrl(trip, document.id)}
                          target="_blank"
                          rel="noreferrer"
                          aria-label={t('docs.openFile', { name: document.name })}
                        >
                          {document.name}
                        </a>
                      ) : (
                        <span>{document.name}</span>
                      )}
                      <span className="quiet">{meta.join(' · ')}</span>
                    </div>
                    {!locked && (
                      <>
                        <button
                          type="button"
                          className="link-button"
                          aria-label={t('docs.edit', { name: document.name })}
                          onClick={() => {
                            setEditing({
                              document,
                              kind: document.kind,
                              file: null,
                              traveller: document.traveller,
                            });
                          }}
                        >
                          ✎
                        </button>
                        <button
                          type="button"
                          className="link-button"
                          aria-label={t('docs.remove', { name: document.name })}
                          onClick={() => {
                            setRemoving(document);
                          }}
                        >
                          ×
                        </button>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {files.length > 0 &&
            workerActive(navigator) &&
            (kept ? (
              <p className="docs-ok" role="status">
                {t('docs.offlineDone')}
              </p>
            ) : (
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  keepFiles(files.map((d) => documentFileUrl(trip, d.id))).then(
                    setKept,
                    ignore,
                  );
                }}
              >
                {t('docs.offline')}
              </button>
            ))}
        </div>
      </div>
      {editing && (
        <DocumentDialog
          trip={trip}
          screen={screen}
          editing={editing}
          onClose={() => {
            setEditing(null);
          }}
          onSaved={() => {
            setEditing(null);
            onChanged();
          }}
        />
      )}
      {removing && (
        <ConfirmDialog
          open
          title={t('docs.removeTitle')}
          body={t('docs.removeBody', { name: removing.name })}
          confirmLabel={t('docs.remove', { name: removing.name })}
          onCancel={() => {
            setRemoving(null);
          }}
          onConfirm={() => {
            setRemoving(null);
            removeDocument(trip, removing.id).then(onChanged, ignore);
          }}
        />
      )}
    </section>
  );
}

/** Adds or changes a document: what it is, a note, its expiry, its stop, who sees it, its file. */
function DocumentDialog({
  trip,
  screen,
  editing,
  onClose,
  onSaved,
}: {
  trip: string;
  screen: DocsScreen;
  editing: Editing;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const ids = useId();
  const picker = useRef<HTMLInputElement>(null);
  const was = editing.document;
  const [kind, setKind] = useState<DocumentKind>(editing.kind);
  const [name, setName] = useState(
    was?.name ?? editing.file?.name ?? t(`docs.kinds.${editing.kind}`),
  );
  const [note, setNote] = useState(was?.note ?? '');
  const [validFrom, setValidFrom] = useState<string | null>(was?.validFrom ?? null);
  const [validTo, setValidTo] = useState<string | null>(was?.validTo ?? null);
  const [stop, setStop] = useState<string | null>(was?.stop ?? null);
  const [only, setOnly] = useState(was?.private ?? false);
  const [whose, setWhose] = useState<string | null>(editing.traveller);
  const [file, setFile] = useState<File | null>(editing.file);
  const [dropFile, setDropFile] = useState(false);
  // What it cost — the insurance, the visa — in the money it was paid in, the trip's at first.
  const paidIn = was?.paid ?? was?.price ?? null;
  const [price, setPrice] = useState<number | null>(paidIn?.minor ?? null);
  const [currency, setCurrency] = useState(paidIn?.currency ?? screen.currency);
  const currencies = [
    ...new Set([
      currency,
      screen.currency,
      ...screen.stops.flatMap((one) => (one.currency === null ? [] : [one.currency])),
    ]),
  ];
  // Once kept, a second try changes that document rather than adding another.
  const [saved, setSaved] = useState<string | null>(was?.id ?? null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failed, setFailed] = useState(false);
  const [sending, setSending] = useState(false);

  const save = () => {
    setSending(true);
    setFailed(false);
    const input = {
      kind,
      name: name.trim(),
      note: note.trim(),
      validFrom,
      validTo,
      stop,
      private: only,
      traveller: whose,
      price,
      currency: currency === screen.currency ? null : currency,
    };
    const kept = saved ? updateDocument(trip, saved, input) : addDocument(trip, input);
    kept
      .then((document) => {
        setSaved(document.id);
        if (file) return uploadDocumentFile(trip, document.id, file);
        return dropFile ? removeDocumentFile(trip, document.id) : null;
      })
      .then(onSaved, (error: unknown) => {
        if (error instanceof InvalidInput) {
          setErrors(
            Object.fromEntries(
              error.fields.map((f) => [
                f.field,
                // Valid to a day before it is valid from: said as days, not as amounts.
                f.problem === 'reversed' ? t('docs.validReversed') : t(problemKey(f.problem)),
              ]),
            ),
          );
        } else {
          setFailed(true);
        }
      })
      .finally(() => {
        setSending(false);
      });
  };

  return (
    <Dialog open title={t(was ? 'docs.change' : 'docs.add')} onClose={onClose}>
      <div className="form">
        <Pick
          label={t('docs.what')}
          options={documentKinds.map((k) => ({ value: k, label: t(`docs.kinds.${k}`) }))}
          value={kind}
          onChange={setKind}
        />
        <div className="field">
          <label className="field-label" htmlFor={`${ids}-name`} data-required>
            {t('docs.name')}
          </label>
          <input
            id={`${ids}-name`}
            className="text-input"
            type="text"
            value={name}
            maxLength={200}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
          {errors.name && <p className="field-error">{errors.name}</p>}
        </div>
        <div className="field">
          <label className="field-label" htmlFor={`${ids}-note`}>
            {t('docs.note')}
          </label>
          <textarea
            id={`${ids}-note`}
            className="text-input"
            rows={2}
            value={note}
            maxLength={500}
            onChange={(event) => {
              setNote(event.target.value);
            }}
          />
        </div>
        {/* The days it is valid: a policy from and to, a passport to a day alone. */}
        <DateRangeInput
          label={t('docs.valid')}
          fromLabel={t('docs.validFrom')}
          toLabel={t('docs.validTo')}
          value={{ from: validFrom, to: validTo }}
          onChange={(range) => {
            setValidFrom(range.from);
            setValidTo(range.to);
          }}
        />
        {errors.validTo && <p className="field-error">{errors.validTo}</p>}
        <AmountField
          key={currency}
          label={t('docs.price')}
          value={price}
          onChange={setPrice}
          currency={currency}
          currencies={currencies}
          onCurrency={(code: string) => {
            setCurrency(code);
            setPrice(null);
          }}
          error={errors.price ?? errors.currency}
        />
        {screen.travellers.length > 0 && (
          <Pick
            label={t('docs.whose')}
            options={[
              { value: '', label: t('docs.mine') },
              ...screen.travellers.map((one) => ({ value: one.id, label: one.name })),
            ]}
            value={whose ?? ''}
            searchable={false}
            onChange={(next) => {
              setWhose(next === '' ? null : next);
            }}
          />
        )}
        <Pick
          label={t('docs.stop')}
          options={screen.stops.map((s) => ({ value: s.id, label: s.city }))}
          value={stop}
          placeholder={t('docs.noStop')}
          onChange={setStop}
          onClear={() => {
            setStop(null);
          }}
        />
        {(was === null || was.mine) && (
          <label className="check">
            <input
              type="checkbox"
              checked={only}
              onChange={(event) => {
                setOnly(event.target.checked);
              }}
            />
            {t('docs.private')}
          </label>
        )}
        <div className="field">
          <p className="field-label">{t('docs.file')}</p>
          <input
            ref={picker}
            type="file"
            accept="application/pdf,image/*"
            hidden
            tabIndex={-1}
            aria-hidden="true"
            data-testid="doc-file"
            onChange={(event) => {
              setFile(event.target.files?.[0] ?? null);
            }}
          />
          {file ? (
            <p className="quiet">{t('docs.attached', { name: file.name })}</p>
          ) : (
            was?.file &&
            !dropFile && (
              <p className="quiet">
                {t('docs.kept')}{' '}
                <button
                  type="button"
                  className="link-button"
                  onClick={() => {
                    setDropFile(true);
                  }}
                >
                  {t('docs.removeFile')}
                </button>
              </p>
            )
          )}
          <button
            type="button"
            className="button"
            onClick={() => {
              picker.current?.click();
            }}
          >
            {t(file || was?.file ? 'docs.replace' : 'docs.attach')}
          </button>
          {errors.file && <p className="field-error">{errors.file}</p>}
        </div>
        {failed && <p role="alert">{t('docs.failed')}</p>}
        <div className="dialog-actions">
          <button
            type="button"
            className="button button-primary"
            disabled={sending}
            onClick={save}
          >
            {t('docs.save')}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

/** What to do before leaving: shared by the trip, ticked as done. */
function BeforeList({
  trip,
  screen,
  onChanged,
}: {
  trip: string;
  screen: DocsScreen;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  // A trip read only: said, nothing ticked, added or changed.
  const locked = useReadOnly();
  const ids = useId();
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const days = differenceInCalendarDays(new Date(`${screen.start}T00:00`), new Date());
  const usual = ['bank', 'card', 'maps', 'esim'];

  const add = (event: SyntheticEvent) => {
    event.preventDefault();
    addBeforeItem(trip, text).then(
      () => {
        setText('');
        setError(null);
        onChanged();
      },
      (problem: unknown) => {
        setError(
          problem instanceof InvalidInput
            ? problem.fields.map((f) => t(problemKey(f.problem))).join(' ')
            : t('docs.failed'),
        );
      },
    );
  };

  return (
    <section className="panel docs-before" aria-labelledby={`${ids}-title`}>
      <h2 id={`${ids}-title`}>
        {days >= 0 ? t('docs.beforeIn', { count: days }) : t('docs.before')}
      </h2>
      {screen.before.length === 0 ? (
        !locked && (
          <button
            type="button"
            className="button"
            onClick={() => {
              usual
                .reduce<Promise<unknown>>(
                  (done, key) =>
                    done.then(() => addBeforeItem(trip, t(`docs.usualItems.${key}`))),
                  Promise.resolve(),
                )
                .then(onChanged, ignore);
            }}
          >
            {t('docs.usual')}
          </button>
        )
      ) : (
        <ul>
          {screen.before.map((item) => (
            <li key={item.id}>
              <label className="check">
                <input
                  type="checkbox"
                  checked={item.done}
                  disabled={locked}
                  onChange={(event) => {
                    updateBeforeItem(trip, item.id, item.text, event.target.checked).then(
                      onChanged,
                      ignore,
                    );
                  }}
                />
                <span className={item.done ? 'docs-done' : ''}>{item.text}</span>
              </label>
              {!locked && (
                <button
                  type="button"
                  className="link-button"
                  aria-label={t('docs.removeItem', { text: item.text })}
                  onClick={() => {
                    removeBeforeItem(trip, item.id).then(onChanged, ignore);
                  }}
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {!locked && (
        <form className="docs-add" onSubmit={add}>
          <div className="field">
            <label className="field-label" htmlFor={`${ids}-new`}>
              {t('docs.newItem')}
            </label>
            <input
              id={`${ids}-new`}
              className="text-input"
              type="text"
              value={text}
              maxLength={200}
              onChange={(event) => {
                setText(event.target.value);
              }}
            />
            {error && <p className="field-error">{error}</p>}
          </div>
          <button type="submit" className="button">
            {t('docs.addItem')}
          </button>
        </form>
      )}
      <Link className="link-button" to={`/trips/${trip}/lists`}>
        {t('lists.open')} →
      </Link>
    </section>
  );
}
